import { IsDefined } from 'class-validator';

import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model } from 'src/core/services/data/JayDataContext';
import { ContextService } from 'src/core/services/context.service';
import { StatusTexts } from "src/app/shared/status-texts";

export class InvoiceListDataService extends DataService<typeof Model.Accounting.Logic.ViewModels.InvoiceSummary, Model.Accounting.Logic.ViewModels.InvoiceSummary, InvoiceSummary> {
	protected map(entity: Model.Accounting.Logic.ViewModels.InvoiceSummary): InvoiceSummary {
		return new InvoiceSummaryModel(entity);
	}

	static make(context?: ContextService): InvoiceListDataService {
		return new InvoiceListDataService(context, ctx => ctx.InvoiceSummaries);
	}

	async getPaymentInvoices(paymentId: number): Promise<InvoiceSummary[]> {
		const container = await this.contextService.current();
		const invoices = await container.GetPaymentInvoices(paymentId).toArray();

		return invoices.map(m => this.map(m));
	}
}

export class InvoiceSummaryModel extends EntityModel<Model.Accounting.Logic.ViewModels.InvoiceSummary, InvoiceSummary> implements InvoiceSummary {
	@property<InvoiceSummaryModel>(o => o.entity.id, (e, value) => e.entity.id = value)
	public id: number;

	@property<InvoiceSummaryModel>(o => o.entity.invoiceNumber, (e, value) => e.entity.invoiceNumber = value)
	public invoiceNumber: string;

	@property<InvoiceSummaryModel>(o => o.entity.invoiceReferenceNumber, (e, value) => e.entity.invoiceReferenceNumber = value)
	public invoiceReferenceNumber: string;

	@property<InvoiceSummaryModel>(o => o.entity.accountingTarget, (e, value) => e.entity.accountingTarget = value)
	public accountingTarget: number;

	@property<InvoiceSummaryModel>(o => o.entity.type, (e, value) => e.entity.type = value)
	public type: number;

	get typeText(): string {
		switch (this.type) {
			case 0:
				return 'Invoice';
			case 1:
				return 'Credit Note';
			case 2:
				return 'Debit Note';
		}

		return '';
	}

	@property<InvoiceSummaryModel>(o => parseFloat(o.entity.totalNet), (e, value) => e.entity.totalNet = value)
	public totalNet: number;

	@property<InvoiceSummaryModel>(o => parseFloat(o.entity.totalGross), (e, value) => e.entity.totalGross = value)
	public totalGross: number;

	@property<InvoiceSummaryModel>(o => parseFloat(o.entity.paid), (e, value) => e.entity.paid = value)
	public paid: number;

	@property<InvoiceSummaryModel>(o => o.entity.billingDate, (e, value) => e.entity.billingDate = value)
	public billingDate: Date;

	@property<InvoiceSummaryModel>(o => o.entity.status, (e, value) => e.entity.status = value)
	public status: number;

	get statusText(): string {
		return StatusTexts.getInvoiceStatusFromNumber(this.status);
	}

	// TODO: validate deadline
	@IsDefined({ message: 'Deadline is required' })
	@property<InvoiceSummaryModel>(o => o.entity.dueDate, (e, value) => e.entity.dueDate = value)
	public dueDate: Date;

	@property<InvoiceSummaryModel>(o => o.entity.currencyId, (e, value) => e.entity.currencyId = value)
	public currencyId: number;
	@property<InvoiceSummaryModel>(o => o.entity.currencyIsoCode, (e, value) => e.entity.currencyIsoCode = value)
	public currencyIsoCode: string;
	@property<InvoiceSummaryModel>(o => o.entity.currencyFormat, (e, value) => e.entity.currencyFormat = value)
	public currencyFormat: string;

	@property<InvoiceSummaryModel>(o => o.entity.clientId, (e, value) => e.entity.clientId = value)
	public clientId?: number;
	@property<InvoiceSummaryModel>(o => o.entity.clientDisplayName, (e, value) => e.entity.clientDisplayName = value)
	public clientDisplayName: string;

	@property<InvoiceSummaryModel>(o => o.entity.agentId, (e, value) => e.entity.agentId = value)
	public agentId?: number;
	@property<InvoiceSummaryModel>(o => o.entity.agentName, (e, value) => e.entity.agentName = value)
	public agentName: string;

	@property<InvoiceSummaryModel>(o => o.entity.supplierId, (e, value) => e.entity.supplierId = value)
	public supplierId?: number;
	@property<InvoiceSummaryModel>(o => o.entity.supplierTitle, (e, value) => e.entity.supplierTitle = value)
	public supplierTitle?: string;

	@property<InvoiceSummaryModel>(o => o.entity.quoteId, (e, value) => e.entity.quoteId = value)
	public quoteId?: number;
	@property<InvoiceSummaryModel>(o => o.entity.quote, (e, value) => e.entity.quote = value)
	public quote: any;
}



// WEBPACK FOOTER //
// ./src/app/accounting/shared/invoices/list/invoice-list.data.ts